import CommunityLocationMap from '@/components/CommunityLocationMap/CommunityLocationMap';
import ArrowRightIcon from '@/components/Icons/ArrowRightIcon';
import ClockIcon from '@/components/Icons/ClockIcon';
import LocationIcon from '@/components/Icons/LocationIcon';
import MailIcon from '@/components/Icons/MailIcon';
import PhoneIcon from '@/components/Icons/PhoneIcon';
import OfficeHours from '@/components/OfficeHours/OfficeHours';
import { useAppContext } from '@/contexts/AppContext';
import { formatPhoneNumber } from '@/utils/phone';
import { Link } from '@tanstack/react-router';
import type { FC } from 'react';

interface Props {
  unitId: number;
  title: string;
  detail: string;
  showScheduleTourLink?: boolean;
  className?: string;
}

const CommunityContactInformation: FC<Props> = ({
  unitId,
  title,
  detail,
  showScheduleTourLink,
  className,
}) => {
  const { community } = useAppContext();

  return (
    <div className={className}>
      <h2 className="text-3xl font-bold text-gray-900 mb-6">{title}</h2>
      <p className="text-lg text-gray-600 mb-8">{detail}</p>

      <div className="mb-8">
        <Link
          to="/application-requirements"
          className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
        >
          View Application Requirements
          <ArrowRightIcon />
        </Link>
      </div>

      <div className="space-y-6">
        {community.address ? (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <LocationIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Visit Us</h3>
              <p className="text-gray-600">
                {community.address.addr1}
                {community.address.addr2 ? ' ' + community.address.addr2 : ''}
                <br />
                {community.address.city}, {community.address.state} {community.address.postalCode}
              </p>
            </div>
          </div>
        ) : null}

        {community.contactInformation?.phone ? (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <PhoneIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Call or Text Us</h3>
              <a
                href={`tel:${community.contactInformation.phone}`}
                className="text-primary-600 hover:text-primary-700 transition-colors"
              >
                {formatPhoneNumber(community.contactInformation.phone)}
              </a>
            </div>
          </div>
        ) : null}

        {community.contactInformation?.email ? (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <MailIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Email Us</h3>
              <a
                href={`mailto:${community.contactInformation.email}`}
                className="text-primary-600 hover:text-primary-700 transition-colors"
              >
                {community.contactInformation.email}
              </a>
            </div>
          </div>
        ) : null}

        <div className="flex items-start">
          <div className="flex-shrink-0">
            <ClockIcon />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">Office Hours</h3>
            <OfficeHours className="text-gray-600 space-y-1" />
          </div>
        </div>
      </div>

      {showScheduleTourLink ? (
        <div className="my-8 bg-primary-50 border border-primary-200 rounded-none p-4 flex flex-col gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Haven&apos;t Toured Yet?</h3>
          <div>
            We recommend scheduling a tour before applying to ensure our community is the right fit
            for you.
          </div>
          <Link
            to="/contact"
            search={{ unitId }}
            className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
          >
            Schedule a Tour
            <ArrowRightIcon />
          </Link>
        </div>
      ) : (
        <div className="mt-8 h-64 bg-gray-300 rounded-none overflow-hidden">
          <CommunityLocationMap />
        </div>
      )}
    </div>
  );
};

export default CommunityContactInformation;
